import { useMemo } from 'react';
import { useUserActiveStatus, useUserLoggedIn } from '~/modules/user';
import type { Conversation } from '../types';
import { getChatParticipants, getOneOnOneChatMember } from '../utils';

/**
 * Hook to get the active status of a conversation. This depends on the
 * conversation type and participants.
 * @param conversation The conversation to get the active status for.
 */
export function useConversationActiveStatus(
    conversation: Conversation | null | undefined
) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const isDirectConversation =
        conversation?.__typename === 'DirectConversation';
    const isGroupChat = isDirectConversation && conversation.IsGroupChat;
    const otherParticipants = useMemo(() => {
        return getChatParticipants(conversation, isLoggedIn ? user.ID : 0);
    }, [conversation, isLoggedIn, user.ID]);
    const memberIDs = useMemo(() => {
        return isDirectConversation
            ? otherParticipants
                  .filter((participant) => participant.__typename === 'Member')
                  .map((participant) => participant.ID)
            : undefined;
    }, [isDirectConversation, otherParticipants]);
    const userStatuses = useUserActiveStatus(memberIDs);

    return useMemo(() => {
        if (!isDirectConversation) {
            return undefined;
        }
        // A group chat status is 'active' if at least one member is active.
        if (isGroupChat) {
            return Object.values(userStatuses).some(
                (status) => status === 'active'
            )
                ? 'active'
                : 'inactive';
        }
        const otherParticipant = getOneOnOneChatMember({
            conversation,
            userID: user.ID,
        });
        return otherParticipant
            ? (userStatuses[otherParticipant.ID] ?? undefined)
            : undefined;
    }, [
        isDirectConversation,
        isGroupChat,
        conversation,
        user.ID,
        userStatuses,
    ]);
}
